import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, Package, CheckCircle2, Truck, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = [
  { key: "pending", label: "Καταχωρήθηκε", icon: Package },
  { key: "confirmed", label: "Επιβεβαιώθηκε", icon: CheckCircle2 },
  { key: "shipped", label: "Στάλθηκε", icon: Truck },
  { key: "delivered", label: "Παραδόθηκε", icon: Home },
];

export const OrderTracking = () => {
  const [orderId, setOrderId] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/orders/track/${orderId.trim()}`);
      setResult(data);
    } catch (ex) {
      setErr(ex?.response?.status === 404 ? "Δεν βρέθηκε παραγγελία με αυτόν τον αριθμό." : "Σφάλμα. Δοκιμάστε ξανά.");
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = result ? STATUS_FLOW.findIndex((s) => s.key === result.status) : -1;

  return (
    <div data-testid="order-tracking-page" className="min-h-screen bg-black text-[color:var(--goldenelia-cream)] py-16 px-6 relative z-10">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-cream)]/65 hover:text-[color:var(--goldenelia-gold)] transition-colors mb-12">
          <ArrowLeft size={14} /> Επιστροφή
        </Link>

        <p className="font-display text-[10px] tracking-[0.55em] uppercase text-[color:var(--goldenelia-gold)] mb-4">
          Παρακολούθηση
        </p>
        <h1 className="font-serif-luxury text-4xl sm:text-5xl gold-gradient-text mb-3 leading-tight">
          Πού είναι η παραγγελία μου;
        </h1>
        <p className="font-body text-[color:var(--goldenelia-cream)]/65 mb-10">
          Εισάγετε τον αριθμό παραγγελίας που λάβατε στο email επιβεβαίωσης.
        </p>

        <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-3 mb-10">
          <input
            data-testid="track-input"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="π.χ. 6824c149-1a42-…"
            className="flex-1 bg-transparent border border-[color:var(--goldenelia-gold)]/40 px-4 py-3 font-mono text-[13px] text-[color:var(--goldenelia-cream)] focus:outline-none focus:border-[color:var(--goldenelia-gold)]"
          />
          <button
            data-testid="track-submit"
            type="submit"
            disabled={loading}
            className="btn-solid-gold !px-6 !py-3 text-[11px] inline-flex items-center justify-center gap-2"
          >
            <Search size={14} /> {loading ? "..." : "Αναζήτηση"}
          </button>
        </form>

        {err && (
          <p data-testid="track-error" className="font-body text-sm text-red-400/90 border border-red-400/30 px-4 py-3 mb-8">
            {err}
          </p>
        )}

        {result && (
          <div data-testid="track-result" className="border border-[color:var(--goldenelia-gold)]/25 bg-black/50 p-6 sm:p-8">
            <p className="font-display text-[10px] tracking-[0.35em] uppercase text-[color:var(--goldenelia-gold)]/85 mb-1">
              Παραγγελία
            </p>
            <p className="font-mono text-[12px] text-[color:var(--goldenelia-cream)]/65 mb-6 break-all">{result.id}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {STATUS_FLOW.map((s, i) => {
                const Icon = s.icon;
                const active = i <= currentIdx;
                return (
                  <div key={s.key} className={`text-center p-3 border ${active ? "border-[color:var(--goldenelia-gold)]/60 bg-[color:var(--goldenelia-gold)]/5" : "border-[color:var(--goldenelia-gold)]/15"}`}>
                    <Icon size={20} className={`mx-auto mb-2 ${active ? "text-[color:var(--goldenelia-gold)]" : "text-[color:var(--goldenelia-cream)]/30"}`} />
                    <p className={`font-display text-[9px] tracking-[0.3em] uppercase ${active ? "text-[color:var(--goldenelia-gold)]" : "text-[color:var(--goldenelia-cream)]/40"}`}>
                      {s.label}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/45">Σύνολο</p>
                <p className="font-serif-luxury text-xl text-[color:var(--goldenelia-gold)]">€{result.total_price}</p>
              </div>
              <div>
                <p className="font-display text-[9px] tracking-[0.3em] uppercase text-[color:var(--goldenelia-cream)]/45">Ημερομηνία</p>
                <p className="font-body text-[color:var(--goldenelia-cream)]/80">{result.created_at ? new Date(result.created_at).toLocaleString("el-GR") : "—"}</p>
              </div>
            </div>

            <p className="font-body text-xs text-[color:var(--goldenelia-cream)]/55 mt-8 italic">
              Αν χρειάζεστε βοήθεια, στείλτε email στο{" "}
              <a href="mailto:admin@goldenelia.gr" className="text-[color:var(--goldenelia-gold)] underline">admin@goldenelia.gr</a>{" "}
              ή WhatsApp <a href="https://wa.me/306984141945" className="text-[color:var(--goldenelia-gold)] underline">+30 698 414 1945</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
